// Ground telegraphs any boss can use (the danger vocabulary, BOSS_GRAMMAR G2/G6): a lane, a ring
// rolling out, a fan. Same shader as Cerberus's (../boss.ts); additive, no depth write, one draw each.

import * as THREE from 'three';

const VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }
`;
// mode 0 lane (u across, v along), 1 ring (annulus at uR), 2 fan (sector over uArc), 3 strike (filled disc filling with uK)
const FRAG = /* glsl */ `
varying vec2 vUv;
uniform float uMode; uniform float uK; uniform float uTime; uniform float uR; uniform float uArc; uniform vec3 uCol;
void main() {
  float a = 0.0;
  if ( uMode < 0.5 ) {
    float edge = 1.0 - smoothstep( 0.4, 0.5, abs( vUv.x - 0.5 ) );
    float rim = smoothstep( 0.36, 0.5, abs( vUv.x - 0.5 ) ) * edge;
    float chev = step( 0.5, fract( vUv.y * 9.0 - abs( vUv.x - 0.5 ) * 2.0 - uTime * 2.2 ) );
    float fill = smoothstep( 0.0, 1.0, uK * 1.15 - vUv.y * 0.15 );
    a = edge * ( 0.18 + 0.32 * chev * fill ) + rim * 0.9;
    a *= smoothstep( 0.0, 0.04, vUv.y ) * smoothstep( 1.0, 0.92, vUv.y );
  } else {
    vec2 c = vUv * 2.0 - 1.0;
    float r = length( c );
    if ( uMode < 1.5 ) {
      a = exp( -pow( ( r - uR ) * 18.0, 2.0 ) ) * 1.2 + step( r, uR ) * 0.06 * smoothstep( uR - 0.3, uR, r );
      a *= step( r, 1.0 );
    } else if ( uMode < 2.5 ) {
      float ang = atan( c.x, -c.y );
      float inside = step( abs( ang ), uArc ) * step( r, 1.0 ) * step( 0.1, r );
      float rim = ( exp( -pow( ( r - 1.0 ) * 30.0, 2.0 ) ) + exp( -pow( ( abs( ang ) - uArc ) * r * 30.0, 2.0 ) ) ) * step( abs( ang ), uArc + 0.02 ) * step( r, 1.02 );
      float stripes = step( 0.5, fract( r * 7.0 - uTime * 1.6 ) );
      a = inside * ( 0.12 + 0.28 * stripes * uK ) + rim * 0.9;
    } else {
      float rim = exp( -pow( ( r - 0.97 ) * 26.0, 2.0 ) ) * ( 0.75 + 0.25 * sin( uTime * 16.0 ) );
      float fill = step( r, uK ) * 0.22 + exp( -pow( ( r - uK ) * 22.0, 2.0 ) ) * 0.6;
      a = ( rim + fill * step( r, 1.0 ) ) * step( r, 1.02 );
    }
  }
  gl_FragColor = vec4( uCol * a, a );
}
`;

export interface Decal { mesh: THREE.Mesh; u: Record<string, THREE.IUniform>; dispose(): void }

/** A flat additive decal lying on the ground (scale x/z to size it; it's a 1 m quad). */
export function groundDecal(scene: THREE.Scene, time: { value: number }, mode: number, col: THREE.Color): Decal {
  const u = { uMode: { value: mode }, uK: { value: 0 }, uTime: time, uR: { value: 0.5 }, uArc: { value: 1.05 }, uCol: { value: col } };
  const g = new THREE.PlaneGeometry(1, 1);
  g.rotateX(-Math.PI / 2);
  const m = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: u, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -4 });
  const mesh = new THREE.Mesh(g, m);
  mesh.visible = false; mesh.renderOrder = 30; mesh.frustumCulled = false;
  scene.add(mesh);
  return { mesh, u, dispose() { scene.remove(mesh); g.dispose(); m.dispose(); } };
}

export const DANGER = new THREE.Color(1.0, 0.28, 0.12);
